/**
 * UniversityCourse — one course: its syllabus, and the lesson reader.
 *
 * Without a lesson in the URL it shows the course's front page. Lessons are
 * marked complete by the learner, not on scroll, because an accidental visit
 * shouldn't count as having read one.
 */
import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { CheckCircle2, Circle, Clock, Loader2 } from 'lucide-react';
import api from '../api/client';
import AppLayout from '../components/AppLayout';
import { AttributeCard, ReadingLink, RichText } from '../components/UniversityParts';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Skeleton } from '../components/ui/skeleton';

function Syllabus({ course, currentSlug }) {
  const done = new Set(course.progress.completed_lessons);
  const { quiz } = course.progress;
  return (
    <nav aria-label="Course syllabus" className="flex flex-col gap-0.5">
      {course.lessons.map((lesson) => (
        <Link
          key={lesson.slug}
          to={`/university/${course.slug}/${lesson.slug}`}
          aria-current={lesson.slug === currentSlug ? 'page' : undefined}
          className={`flex items-start gap-2 rounded px-2 py-1.5 text-xs transition-colors ${
            lesson.slug === currentSlug ? 'bg-primary/10 text-primary' : 'hover:bg-muted'
          }`}
        >
          {done.has(lesson.slug)
            ? <CheckCircle2 className="h-3.5 w-3.5 text-win shrink-0 mt-px" aria-label="Completed" />
            : <Circle className="h-3.5 w-3.5 text-muted-foreground/50 shrink-0 mt-px" />}
          <span className="flex-1">{lesson.title}</span>
          <span className="text-muted-foreground shrink-0">{lesson.minutes}m</span>
        </Link>
      ))}
      <Link
        to={`/university/${course.slug}/quiz`}
        className="flex items-start gap-2 rounded px-2 py-1.5 text-xs hover:bg-muted"
      >
        {quiz.passed
          ? <CheckCircle2 className="h-3.5 w-3.5 text-win shrink-0 mt-px" aria-label="Passed" />
          : <Circle className="h-3.5 w-3.5 text-muted-foreground/50 shrink-0 mt-px" />}
        <span className="flex-1">Course quiz</span>
        {quiz.best_score_pct !== null && (
          <span className="text-muted-foreground shrink-0">{Math.round(quiz.best_score_pct)}%</span>
        )}
      </Link>
    </nav>
  );
}

function CourseFront({ course }) {
  const firstUnread = course.lessons.find(
    (l) => !course.progress.completed_lessons.includes(l.slug),
  );
  const target = firstUnread
    ? `/university/${course.slug}/${firstUnread.slug}`
    : `/university/${course.slug}/quiz`;
  const label = course.progress.lessons_done === 0 ? 'Start course' : (firstUnread ? 'Continue' : 'Take the quiz');

  return (
    <div>
      <p className="text-sm leading-relaxed text-foreground/90 mb-4">{course.summary}</p>
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground mb-5">
        {course.role && <span>Usual job in a screener: <span className="text-foreground">{course.role}</span></span>}
        <span>{course.lesson_count} lessons</span>
        {course.attributes.length > 0 && <span>{course.attributes.length} attributes</span>}
        <span>{course.minutes} min</span>
        <span>Quiz: {course.pass_pct}% to pass</span>
      </div>
      {course.progress.completed && (
        <p className="mb-4 flex items-center gap-2 text-sm text-win">
          <CheckCircle2 className="h-4 w-4" /> You&apos;ve completed this course.
        </p>
      )}
      <Button asChild size="sm"><Link to={target}>{label}</Link></Button>
    </div>
  );
}

function LessonView({ course, lesson, onComplete, completing }) {
  const { hash } = useLocation();
  const [search, setSearch] = useState('');
  const index = course.lessons.findIndex((l) => l.slug === lesson.slug);
  const prev = course.lessons[index - 1];
  const next = course.lessons[index + 1];
  const nextPath = next ? `/university/${course.slug}/${next.slug}` : `/university/${course.slug}/quiz`;
  const isDone = course.progress.completed_lessons.includes(lesson.slug);

  const q = search.trim().toLowerCase();
  const cards = useMemo(() => (q
    ? lesson.cards.filter((c) => `${c.alias} ${c.what}`.toLowerCase().includes(q))
    : lesson.cards), [lesson.cards, q]);

  // Search results link straight to a card, which only exists after loading.
  useEffect(() => {
    if (!hash) {
      window.scrollTo(0, 0);
      return;
    }
    document.getElementById(hash.slice(1))?.scrollIntoView();
  }, [hash, lesson.slug]);

  return (
    <article>
      <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground mb-1">
        <Clock className="h-3 w-3" />{lesson.minutes} min read
      </div>
      <h2 className="text-lg font-semibold mb-3">{lesson.title}</h2>

      <div className="flex flex-col gap-3 mb-5">
        {lesson.body.map((paragraph) => (
          <p key={paragraph} className="text-sm leading-relaxed text-foreground/90">
            <RichText text={paragraph} />
          </p>
        ))}
      </div>

      {lesson.key_points.length > 0 && (
        <div className="rounded-lg border border-primary/30 bg-primary/5 p-3 mb-5">
          <div className="text-[10px] font-semibold uppercase tracking-wide text-primary mb-1.5">
            Key points
          </div>
          <ul className="flex flex-col gap-1 list-disc pl-4 text-sm">
            {lesson.key_points.map((point) => <li key={point}><RichText text={point} /></li>)}
          </ul>
        </div>
      )}

      {lesson.cards.length > 3 && (
        <Input
          placeholder="Filter this lesson's attributes…"
          aria-label="Filter this lesson's attributes"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="text-xs h-8 mb-3 max-w-xs"
        />
      )}
      {cards.length > 0 && (
        <div className="flex flex-col gap-3 mb-6">
          {cards.map((card) => <AttributeCard key={card.alias} card={card} />)}
        </div>
      )}
      {q && cards.length === 0 && (
        <p className="text-xs text-muted-foreground mb-6">No attribute in this lesson matches.</p>
      )}

      <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border pt-4">
        {prev
          ? <Button asChild size="sm" variant="secondary"><Link to={`/university/${course.slug}/${prev.slug}`}>Previous</Link></Button>
          : <span />}
        <Button size="sm" onClick={() => onComplete(lesson.slug, nextPath)} disabled={completing}>
          {completing && <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />}
          {isDone ? (next ? 'Next lesson' : 'Go to the quiz') : (next ? 'Complete and continue' : 'Complete and take the quiz')}
        </Button>
      </div>
    </article>
  );
}

export default function UniversityCourse() {
  const { course: courseSlug, lesson: lessonSlug } = useParams();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [completing, setCompleting] = useState(false);

  useEffect(() => {
    setCourse(null);
    setNotFound(false);
    api.get(`/university/courses/${courseSlug}`)
      .then((r) => setCourse(r.data))
      .catch(() => setNotFound(true));
  }, [courseSlug]);

  const lesson = course && lessonSlug
    ? course.lessons.find((l) => l.slug === lessonSlug)
    : null;

  const handleComplete = async (slug, nextPath) => {
    if (course.progress.completed_lessons.includes(slug)) {
      navigate(nextPath);
      return;
    }
    setCompleting(true);
    try {
      await api.post(`/university/lessons/${slug}/complete`);
      setCourse((c) => ({
        ...c,
        progress: {
          ...c.progress,
          completed_lessons: [...c.progress.completed_lessons, slug],
          lessons_done: c.progress.lessons_done + 1,
        },
      }));
      navigate(nextPath);
    } catch {
      toast.error('Couldn’t save your progress. Try again.');
    } finally {
      setCompleting(false);
    }
  };

  return (
    <AppLayout backTo="/university" backLabel="← University" title={course?.title}>
      <div className="max-w-5xl mx-auto p-4 md:p-6">
        {notFound && <p className="text-sm text-muted-foreground">That course couldn&apos;t be loaded.</p>}
        {!course && !notFound && <Skeleton className="h-64 rounded-lg" />}

        {course && (
          <>
            <div className="flex flex-wrap items-center gap-2 mb-4">
              <h1 className="text-xl font-semibold">{course.title}</h1>
              {course.progress.completed && <Badge variant="win">Completed</Badge>}
            </div>

            <div className="flex flex-col md:flex-row gap-6">
              <aside className="md:w-60 shrink-0 md:order-2">
                <Syllabus course={course} currentSlug={lessonSlug} />
                <div className="mt-4 hidden md:flex flex-col gap-1.5">
                  <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                    Further reading
                  </div>
                  {course.reading.map((link) => <ReadingLink key={link.key} link={link} />)}
                </div>
              </aside>

              <div className="flex-1 min-w-0 md:order-1">
                {lesson && (
                  <LessonView
                    key={lesson.slug}
                    course={course}
                    lesson={lesson}
                    onComplete={handleComplete}
                    completing={completing}
                  />
                )}
                {!lesson && !lessonSlug && <CourseFront course={course} />}
                {!lesson && lessonSlug && (
                  <p className="text-sm text-muted-foreground">
                    That lesson doesn&apos;t exist in this course. Pick one from the syllabus.
                  </p>
                )}

                {/* On a phone the sidebar stacks first, so reading goes after the lesson. */}
                <div className="mt-6 flex md:hidden flex-col gap-1.5">
                  <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                    Further reading
                  </div>
                  {course.reading.map((link) => <ReadingLink key={link.key} link={link} />)}
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </AppLayout>
  );
}
